import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Calculator, TrendingUp } from 'lucide-react';

const COLOR_NAMES = {
  'RAL7016': 'Anthrazitgrau',
  'RAL8017': 'Schokoladenbraun',
  'RAL9006': 'Weißaluminium',
  'RAL9016': 'Verkehrsweiß'
};

const PriceDisplay = ({ priceData, selectedModel, configurationType, length, height, postSize, includePosts, selectedColor }) => {
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (priceData) {
      setAnimate(true);
      const timer = setTimeout(() => setAnimate(false), 500);
      return () => clearTimeout(timer);
    }
  }, [priceData]);

  if (!selectedModel || !priceData || (!priceData.hasFence && !priceData.hasGates)) {
    return (
      <Card className="bg-gradient-to-br from-gray-50 to-gray-100">
        <CardContent className="py-12 text-center">
          <Calculator className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">Wählen Sie ein Modell und geben Sie Konfiguration ein</p>
        </CardContent>
      </Card>
    );
  }

  const { fenceData, gatesData, grandTotal } = priceData;

  return (
    <Card className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 border-2 border-emerald-200 transition-all duration-500 hover:shadow-2xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-emerald-900">
          <TrendingUp className="w-6 h-6" />
          Preisberechnung
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="bg-white/80 backdrop-blur p-4 rounded-lg space-y-3">
          <div className="flex justify-between items-center pb-2 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-600">Modell</span>
            <span className="text-sm font-bold text-gray-900">{selectedModel.name}</span>
          </div>
          
          {/* Color Display */}
          {selectedColor && (
            <div className="flex justify-between items-center pb-2 border-b border-gray-200">
              <span className="text-sm font-medium text-gray-600">Farbe</span>
              <span className="text-sm font-bold text-gray-900">{selectedColor} - {COLOR_NAMES[selectedColor]}</span>
            </div>
          )}
          
          {/* Fence Section */}
          {fenceData && (
            <>
              {fenceData.panelDetails ? (
                /* Panels Only Mode */
                <>
                  {fenceData.panelDetails.map((panel) => (
                    <div key={panel.index} className="flex justify-between items-center pb-2 border-b border-gray-200">
                      <span className="text-sm text-gray-500">
                        Zaun #{panel.index}: {panel.width}×{panel.height} cm ({panel.quantity}x)
                      </span>
                      <span className="text-sm font-bold text-gray-900">€{panel.subtotal}</span>
                    </div>
                  ))}
                  
                  <div className="flex justify-between items-center pb-2 border-b-2 border-emerald-300">
                    <span className="text-sm font-semibold text-gray-700">
                      Zaunelemente Gesamt ({fenceData.totalPanelCount} Stück)
                    </span>
                    <span className="text-sm font-bold text-emerald-700">€{fenceData.totalPrice}</span>
                  </div>
                </>
              ) : (
                /* Fence with Posts Mode */
                <>
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-600">Länge × Höhe</span>
                    <span className="text-sm font-bold text-gray-900">{length} cm × {height} cm</span>
                  </div>

                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-600">Preis pro Meter</span>
                    <span className="text-sm font-bold text-emerald-600">€{fenceData.pricePerMeter}</span>
                  </div>

                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <span className="text-sm font-medium text-gray-600">Zaunelemente</span>
                    <span className="text-sm font-bold text-gray-900">€{fenceData.fencePrice}</span>
                  </div>

                  {includePosts ? (
                    <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                      <span className="text-sm text-gray-500">Steher {postSize} mm ({fenceData.numberOfPosts} × €{fenceData.postPrice})</span>
                      <span className="text-sm font-bold text-gray-900">€{fenceData.postsTotal}</span>
                    </div>
                  ) : (
                    <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                      <span className="text-sm text-gray-500">Ohne Steher</span>
                      <span className="text-sm font-bold text-amber-600">€0</span>
                    </div>
                  )}

                  <div className="flex justify-between items-center pb-2 border-b-2 border-emerald-300">
                    <span className="text-sm font-semibold text-gray-700">Zaun Gesamt</span>
                    <span className="text-sm font-bold text-emerald-700">€{fenceData.totalPrice}</span>
                  </div>
                </>
              )}
            </>
          )}

          {/* Gates Section */}
          {gatesData && gatesData.totalGatesPrice > 0 && (
            <>
              {gatesData.pedestrian && Array.isArray(gatesData.pedestrian) && gatesData.pedestrian.map((gate) => (
                <div key={`ped-${gate.index}`} className="flex justify-between items-center pb-2 border-b border-gray-200">
                  <span className="text-sm text-gray-500">
                    Gehtüre #{gate.index}: {gate.width}×{gate.height} cm ({gate.quantity}x à €{gate.price})
                  </span>
                  <span className="text-sm font-bold text-gray-900">€{gate.subtotal}</span>
                </div>
              ))}

              {gatesData.double && Array.isArray(gatesData.double) && gatesData.double.map((gate) => (
                <div key={`dbl-${gate.index}`} className="flex justify-between items-center pb-2 border-b border-gray-200">
                  <span className="text-sm text-gray-500">
                    Doppelflügeltor #{gate.index}: {gate.width}×{gate.height} cm ({gate.quantity}x à €{gate.price})
                  </span>
                  <span className="text-sm font-bold text-gray-900">€{gate.subtotal}</span>
                </div>
              ))}

              {gatesData.sliding && Array.isArray(gatesData.sliding) && gatesData.sliding.map((gate) => (
                <div key={`sld-${gate.index}`} className="flex justify-between items-center pb-2 border-b border-gray-200">
                  <span className="text-sm text-gray-500">
                    Schiebetor #{gate.index}: {gate.width}×{gate.height} cm ({gate.quantity}x à €{gate.price})
                  </span>
                  <span className="text-sm font-bold text-gray-900">€{gate.subtotal}</span>
                </div>
              ))}

              {gatesData.gatePostsPrice > 0 && (
                <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                  <span className="text-sm text-gray-500">
                    Torsteher {priceData.gatePostSize} ({gatesData.numberOfGatePosts} × €{gatesData.gatePostUnitPrice})
                  </span>
                  <span className="text-sm font-bold text-gray-900">€{gatesData.gatePostsPrice}</span>
                </div>
              )}

              <div className="flex justify-between items-center pb-2 border-b-2 border-emerald-300">
                <span className="text-sm font-semibold text-gray-700">Tore Gesamt</span>
                <span className="text-sm font-bold text-emerald-700">€{gatesData.totalGatesPrice + gatesData.gatePostsPrice}</span>
              </div>
            </>
          )}

          {/* Accessories Section */}
          {priceData.accessoriesData && priceData.accessoriesData.total > 0 && (
            <>
              {priceData.accessoriesData.details.map((item, index) => (
                <div key={`acc-${index}`} className="flex justify-between items-center pb-2 border-b border-gray-200">
                  <span className="text-sm text-gray-500">
                    {item.name} ({item.variant}) {item.quantity > 1 ? `${item.quantity}x à €${item.unitPrice}` : ''}
                  </span>
                  <span className="text-sm font-bold text-gray-900">€{item.subtotal}</span>
                </div>
              ))}

              <div className="flex justify-between items-center pb-2 border-b-2 border-emerald-300">
                <span className="text-sm font-semibold text-gray-700">Zubehör Gesamt</span>
                <span className="text-sm font-bold text-emerald-700">€{priceData.accessoriesData.total}</span>
              </div>
            </>
          )}
        </div>

        {/* Transport Info */}
        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center mb-2">
            <span className="text-sm font-medium text-blue-900">Transportkosten</span>
          </div>
          <p className="text-xs text-blue-700">
            Die Transportkosten hängen von der Entfernung und der Menge ab.
          </p>
          <p className="text-xs text-blue-700 mt-1">
            Für weitere Informationen senden Sie uns bitte eine Anfrage an{' '}
            <a href="mailto:hello@smart-mak.com" className="font-medium underline">hello@smart-mak.com</a>
          </p>
        </div>

        <div className={`bg-gradient-to-r from-emerald-600 to-teal-600 p-6 rounded-lg shadow-lg transform transition-all duration-500 ${
          animate ? 'scale-105' : 'scale-100'
        }`}>
          <div className="flex justify-between items-center">
            <span className="text-lg font-bold text-white">Gesamtpreis</span>
            <span className="text-3xl font-bold text-white">€{grandTotal}</span>
          </div>
          <p className="text-xs text-emerald-100 mt-2">
            {configurationType === 'fence-only' && fenceData && (
              fenceData.panelDetails
                ? `${fenceData.totalPanelCount} Zaunelemente`
                : includePosts 
                  ? `inkl. ${fenceData.numberOfPanels} Paneele & ${fenceData.numberOfPosts} Steher` 
                  : `inkl. ${fenceData.numberOfPanels} Paneele (ohne Steher)`
            )}
            {configurationType === 'gates-only' && 'Toranlagen'}
            {configurationType === 'fence-and-gates' && 'Zaun & Tore Komplettpaket'}
          </p>
        </div>

        <div className="text-center space-y-1">
          <p className="text-xs text-gray-500 italic">
            Alle Preise inkl. MwSt. Unverbindliches Informationsangebot.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default PriceDisplay;